import { useEffect, useState } from "react";
import { Facebook, Link2, Check, X as CloseIcon, Share2 } from "lucide-react";
import { toast } from "sonner";
import { buildShareUrl, type SharePreviewType } from "@/lib/shareUrl";

interface StickyShareBarProps {
  url: string;
  title: string;
  image?: string;
  slug?: string;
  type?: SharePreviewType;
}

const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const PinterestIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M12 0a12 12 0 0 0-4.373 23.178c-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146A12 12 0 1 0 12 0" />
  </svg>
);

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.83 9.83 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.82 11.82 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.88 11.88 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 0 0-3.48-8.413" />
  </svg>
);

export default function StickyShareBar({ url, title, image, slug, type = "blog" }: StickyShareBarProps) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (dismissed) return;
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? scrollTop / docHeight : 0;
      setVisible(progress >= 0.3 && progress < 0.97);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed]);

  const shareUrl = buildShareUrl({ type, slug, fallbackUrl: url });
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedImage = image ? encodeURIComponent(image) : "";

  const targets = [
    { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, Icon: Facebook },
    { name: "X", href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`, Icon: XIcon },
    { name: "Pinterest", href: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedImage}&description=${encodedTitle}`, Icon: PinterestIcon },
    { name: "WhatsApp", href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`, Icon: WhatsAppIcon },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const isOpen = visible && !dismissed;

  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-40 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        isOpen
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-10 pointer-events-none"
      }`}
      aria-hidden={!isOpen}
    >
      <div className="group flex items-center gap-1 pl-5 pr-2 py-2 rounded-full bg-background/80 backdrop-blur-xl border border-border/60 shadow-[0_20px_60px_-20px_rgba(197,0,124,0.25),0_4px_20px_-4px_rgba(0,0,0,0.08)]">
        <Share2 className="w-3.5 h-3.5 text-primary mr-2.5" strokeWidth={1.5} />
        <span className="font-display italic text-[13px] text-foreground/80 tracking-wide pr-3 mr-1 border-r border-border/60 hidden xs:inline sm:inline">
          Share
        </span>

        <div className="flex items-center gap-0.5">
          {targets.map(({ name, href, Icon }, i) => (
            <a
              key={name}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Share on ${name}`}
              style={{ transitionDelay: isOpen ? `${i * 60 + 200}ms` : "0ms" }}
              className={`relative w-9 h-9 inline-flex items-center justify-center rounded-full text-foreground/60 hover:text-primary hover:bg-primary/5 transition-all duration-300 ${
                isOpen ? "opacity-100 scale-100" : "opacity-0 scale-75"
              }`}
            >
              <Icon className="w-[15px] h-[15px]" />
            </a>
          ))}
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy link"
            style={{ transitionDelay: isOpen ? `${targets.length * 60 + 200}ms` : "0ms" }}
            className={`relative w-9 h-9 inline-flex items-center justify-center rounded-full text-foreground/60 hover:text-primary hover:bg-primary/5 transition-all duration-300 ${
              isOpen ? "opacity-100 scale-100" : "opacity-0 scale-75"
            }`}
          >
            {copied ? <Check className="w-[15px] h-[15px] text-primary" /> : <Link2 className="w-[15px] h-[15px]" strokeWidth={1.75} />}
          </button>
        </div>

        <span className="w-px h-5 bg-border/60 mx-1" aria-hidden="true" />

        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss share bar"
          className="w-7 h-7 inline-flex items-center justify-center rounded-full text-foreground/40 hover:text-foreground hover:bg-muted/60 transition-colors"
        >
          <CloseIcon className="w-3 h-3" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
